import React, { FC } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FoldableContainer } from "@/components/pages/chats/component/booking-info-components/FoldableContainer";
import { billingTransactionService } from "@/services/billing-transactions-service";
import {
  computeBillingBalanceStats,
  formatMinorCurrency,
} from "@/utils/billingBalance";
import { Reservation } from "@/types/reservation";

interface PaymentBalanceBookingInfoProps {
  booking: Reservation;
}

const BALANCE_TRANSACTION_LIMIT = 100;

export const PaymentBalanceBookingInfo: FC<PaymentBalanceBookingInfoProps> = ({
  booking,
}) => {
  const { t } = useTranslation();
  const guestId = booking.guestId;

  const { data, isLoading } = useQuery({
    queryKey: ["chats", "bookingBalance", guestId],
    queryFn: () =>
      billingTransactionService.getBillingTransactions(
        { guestId },
        1,
        BALANCE_TRANSACTION_LIMIT,
      ),
    enabled: Boolean(guestId),
    staleTime: 5 * 60 * 1000,
  });

  const transactions = data?.transactions ?? [];

  if (!guestId || (!isLoading && transactions.length === 0)) {
    return null;
  }

  const stats = computeBillingBalanceStats(transactions);

  const rows = [
    {
      label: t("billing.balance"),
      value: formatMinorCurrency(stats.balanceMinor, stats.currency),
      valueClass: "text-eathy-comfort-1",
    },
    {
      label: t("billing.credit"),
      value: formatMinorCurrency(stats.creditMinor, stats.currency),
      valueClass: "text-emerald-600",
    },
    {
      label: t("billing.debet"),
      value: formatMinorCurrency(stats.debetMinor, stats.currency),
      valueClass: "text-rose-600",
    },
  ];

  return (
    <FoldableContainer titleNode={t("chats.payments")}>
      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex flex-row items-center justify-between gap-3 text-sm"
          >
            <span className="text-neutral-500">{row.label}</span>
            <span className={`font-medium ${row.valueClass}`}>{row.value}</span>
          </div>
        ))}
      </div>
    </FoldableContainer>
  );
};
